import {
  workplaceProfilesListQuerySchema,
  type WorkplaceProfilesListResponse,
  workplaceProfilesListResponseSchema,
} from "@clipboard-health/contract-backend-main";
import {
  useInfiniteGetQuery,
  type UseInfiniteGetQueryOptions,
} from "@src/appV2/api/useInfiniteGetQuery";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { minutesToMilliseconds } from "date-fns";
import { type z } from "zod";

export type GetWorkplacesByIdRequest = z.input<typeof workplaceProfilesListQuerySchema>;
export type GetWorkplacesByIdResponse = WorkplaceProfilesListResponse;
export type WorkplaceData = WorkplaceProfilesListResponse["data"][number];

const GET_WORKPLACES_BY_ID_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces`;

// Query parser in backend main limits the array size at 20 by default
// TODO: https://linear.app/clipboardhealth/issue/ALCH-3913/allow-loading-more-than-20-workplaces-in-required-by-bottom-sheet
const MAX_WORKPLACES_TO_LOAD = 20;

export interface UseGetWorkplacesByIdProps {
  workplaceIds: string[];
}

export function useGetWorkplacesById(
  props: UseGetWorkplacesByIdProps,
  options: UseInfiniteGetQueryOptions<GetWorkplacesByIdResponse> = {}
) {
  const { workplaceIds } = props;

  return useInfiniteGetQuery<
    GetWorkplacesByIdRequest,
    GetWorkplacesByIdResponse,
    GetWorkplacesByIdResponse,
    number
  >({
    url: GET_WORKPLACES_BY_ID_URL,
    queryParams: {
      filter: {
        ids: workplaceIds.slice(0, MAX_WORKPLACES_TO_LOAD),
      },
    },
    requestSchema: workplaceProfilesListQuerySchema,
    responseSchema: workplaceProfilesListResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKPLACES_FAILURE,
    },
    staleTime: options.staleTime ?? minutesToMilliseconds(5),
    getNextPageParam: (_lastPage, allPages) => {
      const currentPageNumber = allPages.length;
      const totalPagesNeeded = Math.ceil(workplaceIds.length / MAX_WORKPLACES_TO_LOAD);

      return currentPageNumber < totalPagesNeeded ? currentPageNumber : undefined;
    },
    nextPageContext: async ({ pageParam }) => {
      const startIndex = pageParam * MAX_WORKPLACES_TO_LOAD;
      const endIndex = Math.min(startIndex + MAX_WORKPLACES_TO_LOAD, workplaceIds.length);

      return {
        queryParams: {
          filter: {
            ids: workplaceIds.slice(startIndex, endIndex),
          },
        },
      };
    },
    ...options,
  });
}
